import { buildProps } from '@jdesign-vue/utils'
import { dropdownProps } from '@jdesign-vue/components/dropdown'
import { useTooltipContentProps } from '@jdesign-vue/components/tooltip'
import { popperContentProps } from '@jdesign-vue/components/popper/src/content'
import { useNamespace } from '@jdesign-vue/hooks'
import type { ExtractPropTypes, PropType, VNode } from 'vue'

const ns = useNamespace('guide')

export interface RectType {
  top: number
  width: number
  left: number
  height: number
}

export interface CoverType {
  type: string
  props: any
}

export type PlacementType =
  | 'top'
  | 'top-start'
  | 'top-end'
  | 'bottom'
  | 'bottom-start'
  | 'bottom-end'
  | 'right'
  | 'right-start'
  | 'right-end'
  | 'left'
  | 'left-start'
  | 'left-end'

  export interface StepType {
    /**
     * 相对于 placement 的偏移量，示例：20 / 默认为12 -- 恰好满足UED对4px距离的要求
     */
    offset?: number
    /**
     * 引导框相对于高亮元素出现的位置
     */
    placement?: PlacementType
    /**
     * 是否出现遮罩层
     */
    mask?: boolean
    /**
     * 当前步骤的标题内容
     */
    title?: string | VNode
    /**
     * 封面
     */
    cover?: VNode
    /**
     * 描述 body
     */
    description?: string | VNode
    /**
     * 高亮目标
     */
    target?: keyof HTMLElementTagNameMap | (() => HTMLElement)
    /**
     * 高亮目标的父节点
     */
    parent?: keyof HTMLElementTagNameMap
    /**
     * title 插槽
     */
    titleSlot?: string
    /**
     * 封面 插槽
     */
    coverSlot?: string
    /**
     * 内容 插槽
     */
    descSlot?: string
    /**
     * 扩展 插槽
     * 支持面板上方插入图片等
     */
    extraSlot?: string
    /**
     * 面板色彩
     * css 色值，当开启 color 时，Guide 面板文案与按钮颜色会反转
     */
    color?: string
  }

  export const guideProps = buildProps({
    placement: dropdownProps.placement,
    appendTo: useTooltipContentProps.appendTo,
    offset: popperContentProps.offset,
    popperStyle: popperContentProps.popperStyle,
    persistent: Boolean,
    transition: {
      type: String,
      default: `${ns.namespace.value}-fade-in-linear`,
    },
    arrowOffset: {
      type: Number,
      default: 5,
    },
    // 外部控制是否显示
    visible: {
      type: Boolean,
      default: false,
    },
    /**
     * @description disable the guide hint
     */
    disabled: Boolean,
    /**
     * @description ignore disabled from Form
     */
    ignoreFormDisabled: Boolean,
    // 是否显示右上角关闭按钮
    closable: {
      type: Boolean,
      default: false,
    },
    // 是否显示蒙层
    mask: {
      type: Boolean,
      default: true,
    },
    showArrow: {
      type: Boolean,
      default: true,
    },
    skip: {
      type: Boolean,
      default: true,
    },
    steps: {
      type: Array as PropType<StepType[]>,
      default: () => [] as StepType[],
      required: true,
    },
    popperClass: String,
    current: {
      type: Number,
      default: 0,
    },
    zIndex: {
      type: Number,
    },
    /**
     * 面板色彩
     * css 色值，当开启 color 时，Guide 面板文案与按钮颜色会反转
     */
    color: {
      type: String,
    },
    /**
     * 是否开启自动侦测
     * 当元素内容有Ajax数据填充导致元素宽高变化时, 可手动开启自动侦测元素功能
     */
    autoDetect: {
      type: Boolean,
      default: false,
    },
    panelClass: {
      type: String,
    },
    /**
     * 定位方式
     */
    positionType: {
      type: String as PropType<'absolute' | 'fixed'>,
      default: 'absolute',
    },
    effect:{
      type:String as PropType<'dark' | 'light'>,
      default:'light'
    }
  } as const)

  export type GuideProps = ExtractPropTypes<typeof guideProps>

  export const guideEmits = {
    // 所有步骤均完成时触发
    finish: (context: { current: number; total: number }) => undefined,
    // 点击关闭/跳过时触发
    close: (context: { current: number; total: number }) => undefined,
    prev: (context: { current: number; total: number }) => undefined,
    next: (context: { current: number; total: number }) => undefined,
  }
